<template>
    <div>
        <!-- Action buttons -->
        <div class="d-flex justify-content-between align-items-center mb-3">
            <div>
                <small class="text-muted">{{ count }} {{ $t('contracts') }}</small>
            </div>
            <div class="d-flex gap-2">
                <button @click="createContract" class="btn btn-sm btn-success">
                    <PlusIcon class="me-1 client-lucide-icon" :size="15" :stroke-width="1.75" aria-hidden="true" />
                    {{ $t('add_contract') }}
                </button>
            </div>
        </div>

        <!-- Contracts Table -->
        <div class="table-responsive bg-white">
            <table class="table mb-0 table-borderless table-striped small">
                <thead>
                    <tr class="text-secondary">
                        <th class="pt-4 pb-3 px-6">{{ $t('contract_number') }}</th>
                        <th class="pt-4 pb-3 px-6">{{ $t('title') }}</th>
                        <th class="pt-4 pb-3 px-6">{{ $t('status') }}</th>
                        <th class="pt-4 pb-3 px-6">{{ $t('amount') }}</th>
                        <th class="pt-4 pb-3 px-6">{{ $t('start_date') }}</th>
                        <th class="pt-4 pb-3 px-6">{{ $t('end_date') }}</th>
                        <th class="pt-4 pb-3 px-6">{{ $t('action') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="contract in contracts" :key="contract.id">
                        <td class="py-5 px-6">
                            <router-link :to="`/contracts/${contract.id}`"><b>{{ contract.contract_number }}</b></router-link>
                        </td>
                        <td class="py-5 px-6">{{ contract.title }}</td>
                        <td class="py-5 px-6">
                            <span class="badge" :class="getStatusBadge(contract.status)">{{ contract.status }}</span>
                        </td>
                        <td class="py-5 px-6">{{ formatMoney(contract.amount) }}</td>
                        <td class="py-5 px-6">{{ formatDate(contract.start_date) }}</td>
                        <td class="py-5 px-6">{{ formatDate(contract.end_date) }}</td>
                        <td class="py-5 px-6">
                            <button @click="viewContract(contract)" style="padding: 5px;margin: 0;font-size: 12px !important;margin-right:2px" class="btn btn-outline-primary btn-xs" :title="$t('view_with_template')">
                                <AppIcon name="bi-file-earmark-text" class="bi bi-file-earmark-text me-1" /> {{ $t('view') }}
                            </button>
                            <button @click="goDetails(contract.id)" style="padding: 5px;margin: 0;font-size: 12px !important;margin-right:2px" class="btn btn-primary btn-xs">
                                <AppIcon name="bi-eye" class="bi bi-eye me-1" /> {{ $t('details') }}
                            </button>
                            <button @click="deleteRow(contract)" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs">
                                <AppIcon name="bi-trash" class="bi bi-trash me-1" style="font-size:12px" /> {{ $t('delete') }}
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
            <div v-if="count > contracts.length" style="text-align:center;margin-top:1rem;margin-bottom:1rem">
                <compact-pagination :pages="pagination" :current-page="page" @change="getContracts" />
            </div>
        </div>

        <table-loading-bar v-if="loading" class="text-center" />
        <div v-if="!loading && contracts.length === 0" class="text-center py-5 my-3 bg-white rounded shadow-sm border" style="border-style: dashed !important;">
            <AppIcon name="bi-file-earmark-text" class="bi bi-file-earmark-text text-muted" style="font-size: 3rem;" />
            <h5 class="mt-3">{{ $t('no_contracts_for_client') || $t('no_contracts_found') }}</h5>
            <button class="btn btn-outline-primary btn-sm mt-2" @click="createContract">
                {{ $t('create_contract_for_client') || $t('add_contract') }}
            </button>
        </div>

        <!-- Modals -->
        <AddEditContractModal ref="addEditModal" :contractData="selectedContract" @saved="onContractSaved" />
        
        <ContractTemplateModal 
            v-if="viewModalIsOpen" 
            @modalClosed="viewModalIsOpen = false" 
            modalId="client-view-contract-modal" 
            :isModalOpen="viewModalIsOpen"  
            :data="viewingContract"
        />

    </div>
</template>

<script>
import helper from '../helper'
import AddEditContractModal from './contract/AddEditContractModal.vue'
import ContractTemplateModal from './contract/ContractTemplateModal.vue'

export default {
    name: 'ContractComponent',
    components: {
        AddEditContractModal,
        ContractTemplateModal
    },
    props: {
        client_id: { type: String, default: null },
        client: { type: Object, default: () => ({}) },
        isDetailClient: { type: Boolean, default: false }
    },
    data() {
        return {
            contracts: [],
            loading: false,
            per_page: 10,
            page: 1,
            count: 0,
            pagination: [],
            selectedContract: null,
            viewModalIsOpen: false,
            viewingContract: null
        }
    },
    mounted() {
        this.getContracts();
    },
    methods: {
        formatMoney(number) { return helper.formatMoney(number); },
        formatDate(date) { return helper.formatDate(date); },
        getStatusBadge(status) {
            const badges = {
                active: 'bg-success',
                pending: 'bg-warning text-dark',
                expired: 'bg-secondary',
                terminated: 'bg-danger',
                draft: 'bg-light text-dark',
                signed: 'bg-info'
            };
            return badges[status] || 'bg-secondary';
        },
        createContract() {
            this.selectedContract = {
                type: 'client',
                linked_id: String(this.client_id)
            };
            this.$refs.addEditModal.show('general', false);
        },
        viewContract(contract) {
            this.viewingContract = contract;
            this.viewModalIsOpen = true;
        },
        goDetails(id) {
            this.$router.push({ path: `/contracts/${id}` });
        },
        onContractSaved() {
            this.getContracts(this.page);
        },
        async getContracts(page) {
            try {
                this.loading = true;
                this.page = page ? page : 1;
                
                const params = new URLSearchParams();
                params.append('page', this.page);
                params.append('per_page', this.per_page);
                params.append('is_template', 'false');
                params.append('type', 'client');
                
                if (this.client_id) {
                    params.append('linked_id', this.client_id);
                }

                const url = `${process.env.VUE_APP_API}/contracts?${params.toString()}`;
                const response = await helper.customGet(url);
                this.contracts = response.data.data;
                this.count = response.data.count;

                if (this.per_page != response.data.per_page) {
                    this.per_page = response.data.per_page;
                }

                this.pagination = [];
                let maxPagination = Math.ceil(this.count / this.per_page);
                for (let i = 1; i <= maxPagination; i++) {
                    this.pagination.push(i);
                }

                this.loading = false;
            } catch (error) {
                this.loading = false;
                alert(error);
            }
        },
        async deleteRow(row) {
            this.$swal.fire({
                title: `${this.$t('are_you_sure')}?`,
                text: this.$t('you_cant_revert'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: this.$t('yes_delete_it')
            }).then(async (result) => {
                if (result.isConfirmed) {
                    await helper.customDelete(`${process.env.VUE_APP_API}/contracts/${row.id}`);
                    this.contracts = helper.withoutDeletedRecords(this.contracts, row.id);
                }
            });
        }
    }
}
</script>
